/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {CheckListPlugin} from '@lexical/react/LexicalCheckListPlugin';
import LexicalClickableLinkPlugin from '@lexical/react/LexicalClickableLinkPlugin';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import {HistoryPlugin} from '@lexical/react/LexicalHistoryPlugin';
import {HorizontalRulePlugin} from '@lexical/react/LexicalHorizontalRulePlugin';
import {ListPlugin} from '@lexical/react/LexicalListPlugin';
import {LinkPlugin} from '@lexical/react/LexicalLinkPlugin';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {TabIndentationPlugin} from '@lexical/react/LexicalTabIndentationPlugin';
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListItemNode, ListNode } from "@lexical/list";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import {useState} from 'react';
import {$getRoot, EditorState, LexicalEditor} from 'lexical';

import {$generateHtmlFromNodes} from '@lexical/html';
import {$generateNodesFromDOM} from '@lexical/html';
import { LexicalComposer} from '@lexical/react/LexicalComposer';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {OnChangePlugin} from '@lexical/react/LexicalOnChangePlugin';
import FloatingLinkEditorPlugin from './FloatingLinkEditor';
import theme from './Theme';
import AutoLink from './AutoLink';
import RTEslimToolbar from './RTEslimToolbar';




// Catch any errors that occur during Lexical updates and log them
// or throw them as needed. If you don't throw them, Lexical will
// try to recover gracefully without losing user data.
function onError(error:any) {
  console.error(error);
}

interface Props {
  onChange(newVal:string):void;
  value:string;
  disabled?:boolean;
  height?:string;
}

export default function RTEslim(props:Props) {
  const initialConfig  = {
    namespace: 'RTEslim', 
    theme,
    onError,
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      AutoLinkNode,
      LinkNode,
      HorizontalRuleNode
    ],
    editorState : (editor: LexicalEditor) => {
      const root = $getRoot()
      const parser = new DOMParser();
      const dom = parser.parseFromString(props.value, "text/html");
      const nodes = $generateNodesFromDOM(editor, dom);
      nodes.forEach(n => root.append(n));
    },
    editable: props.disabled ? false : true
  };

  
  const [floatingAnchorElem, setFloatingAnchorElem] =
    useState<HTMLDivElement | null>(null);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  const renderLinkEditor = () => {
    return floatingAnchorElem ?
    <FloatingLinkEditorPlugin anchorElem={floatingAnchorElem as HTMLElement} />
    :<></>
  }

  // When the editor changes, you can get notified via the
// LexicalOnChangePlugin!
function onChange(editorState: EditorState, editor: LexicalEditor, tags: Set<string>) {
  editorState.read(() => {
    const htmlString = $generateHtmlFromNodes(editor, null);
    props.onChange(htmlString);
  });
}

  return (
    <div className='editor-shell'>
      <LexicalComposer initialConfig={initialConfig}>
        <RTEslimToolbar />
        <RichTextPlugin
          contentEditable={
            <div className='editor-container'>
              <div className="editor-scroller">
                <div className="editor" ref={onRef} style={props.height ? { height : props.height, overflow: "auto", resize: "none" } : {}}>
                  <ContentEditable className='ContentEditable__root' />
                </div>
              </div>
            </div>
          }
          placeholder={<div className='Placeholder__root'></div>}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <TabIndentationPlugin />
        <LexicalClickableLinkPlugin />
        <LinkPlugin />
        <AutoLink />
        <ListPlugin />
        <CheckListPlugin />
        <HorizontalRulePlugin />
        <OnChangePlugin onChange={onChange} />
        <HistoryPlugin />
        { renderLinkEditor() }
      </LexicalComposer>
    </div>
  );
}